'use client'

import { UploadCloud, FileCheck2, Loader, XCircle, Clock } from 'lucide-react'
import Icon from '@/shared/v2/shared/components/Icon'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import { BulkUploadState } from '../hooks/useBulkUpload'
import { useTranslations } from 'next-intl'

const getStateIcon = (state: BulkUploadState) => {
    switch (state) {
        case 'idle':
            return UploadCloud
        case 'working_short':
        case 'working_medium':
            return Loader
        case 'working_long':
            return Clock
        case 'success':
            return FileCheck2
        case 'error':
        case 'canceled':
            return XCircle
        default:
            return Loader
    }
}

type BulkUploadLoaderProps = {
    state: BulkUploadState
}

const BulkUploadLoader = ({ state }: BulkUploadLoaderProps) => {
    const t = useTranslations('root.components.bulkUploadLoader')
    const StateIcon = getStateIcon(state)
    const isSpinning = state === 'working_short' || state === 'working_medium' || state === 'working_long'

    return (
        <div
            data-testid="__bulk-upload-loader__"
            className="flex flex-col items-center justify-center gap-4 text-center"
        >
            <div className="relative flex items-center justify-center w-16 h-16">
                <svg viewBox="0 0 100 100" className="w-full h-full absolute">
                    <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        strokeWidth="5"
                        stroke="rgba(200, 200, 200, 0.2)"
                        strokeLinecap="round"
                    />
                </svg>

                {isSpinning && (
                    <div className="absolute inset-0 animate-spin" data-testid="__bulk-upload-loader-spinner__">
                        <svg viewBox="0 0 100 100" className="w-full h-full">
                            <circle
                                cx="50"
                                cy="50"
                                r="45"
                                fill="none"
                                strokeWidth="5"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeDasharray="283"
                                strokeDashoffset="200"
                                className="text-primary"
                            />
                        </svg>
                    </div>
                )}

                <div className="absolute flex items-center justify-center w-8 h-8 bg-backgroundPrimary rounded-full shadow-md">
                    <Icon
                        data-testid="__bulk-upload-loader-state-icon__"
                        icon={StateIcon}
                        className="w-4 h-4 text-primary"
                    />
                </div>
            </div>

            <div className="flex flex-col items-center gap-2">
                <Paragraph data-testid="__bulk-upload-loader-state-message__">
                    {state === 'working_short' && t('workingShort')}
                    {state === 'working_medium' && t('workingMedium')}
                    {state === 'working_long' && t('workingLong')}
                    {state === 'success' && t('success')}
                    {state === 'error' && t('error')}
                    {state === 'canceled' && t('canceled')}
                    {state === 'idle' && t('idle')}
                </Paragraph>
            </div>
        </div>
    )
}

export default BulkUploadLoader
